import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import i18next from 'i18next'
import { useNavigate } from 'react-router-dom'

import {
  deleteTransactions,
  getTransactions,
  searchDatedTransaction,
  searchTransaction,
  redoTransaction,
  postTransaction
} from '../../store/slices/TransactionsSlice'
import {
  AuditIcon,
  CancelIcon,
  EditIcon,
  EyeIcon,
  PrintIcon,
  SendIcon,
  UndoIcon
} from '../../components/Shared/TableActionIcons'

import CustomPagination from '../../components/Shared/CustomPagination'
import TableLoading from '../../components/Shared/TableLoading'
import CustomTable from '../../components/Shared/CustomTable'
import moment from 'moment'
import ConfirmationModal from '../../components/Shared/ConfirmationModal'
import { MdOutlineCancel } from 'react-icons/md'
import CustomChip from '../../components/Shared/CustomChip'

export const formatDate = dateString => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const formattedHours = String(hours % 12 || 12).padStart(2, '0')
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`
}

export default function TransactionsTable ({
  searchQuery,
  status,
  dates,
  loading,
  type,
  allData,
  onPageChange
}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const lang = i18next.language
  const { t } = useTranslation()

  const [deleteLoading, setDeleteLoading] = useState(false)
  const transactions = allData?.data || []
  const page = allData?.page || 1
  const per_page = allData?.per_page || 10
  const total_pages = allData?.total_pages || 0
  const total_record = allData?.total_record || 0

  const [createDialogVisible, setCreateDialogVisible] = useState(false)
  const [confirmationDialogVisible, setConfirmationDialogVisible] =
    useState(false)
  const [undoDialogVisible, setUndoDialogVisible] = useState(false)
  const [postDialogVisible, setPostDialogVisible] = useState(false)
  const [transId, setTransId] = useState()
  const [transNumber, setTransNumber] = useState('')
  const [selectedTransactionEntries] = useState([])

  const getStatusConfig = status => {
    switch (status) {
      case 'POSTED':
        return { label: 'Posted', chipType: 'success' }
      case 'CANCELLED':
        return { label: 'Cancelled', chipType: 'danger' }
      case 'DRAFT':
      default:
        return { label: 'Draft', chipType: 'warning' }
    }
  }

  const templateBody = rowData => (
    <div className='flex gap-1'>
      <EyeIcon
        onClick={() => {
          navigate('/manager/transactions/view', {
            state: {
              transactionId: rowData?.id,
              transactionDetails: rowData
            }
          })
        }}
        title={t('view')}
      />
      <AuditIcon
        onClick={() => {
          if (!rowData?.journal_entry_number) return
          navigate(
            `/manager/transactions/audit/${encodeURIComponent(
              rowData.journal_entry_number
            )}`
          )
        }}
        title={t('audit_logs')}
      />

      {rowData?.status === 'DRAFT' && (
        <>
          <EditIcon title={t('edit')} onClick={() => handleEdit(rowData)} />
          <SendIcon
            title={t('Post')}
            className='text-green-600'
            onClick={() => {
              setTransId(rowData?.id)
              setTransNumber(rowData?.journal_entry_number)
              setPostDialogVisible(true)
            }}
          />
          {type !== 'opening_balance' && (
            <CancelIcon
              onClick={() => {
                setTransId(rowData?.id)
                setTransNumber(rowData?.journal_entry_number)
                setConfirmationDialogVisible(true)
              }}
              title={t('cancel')}
            />
          )}
        </>
      )}

      {rowData?.status === 'POSTED' && (
        <>
          <PrintIcon
            title={t('print_journal_entries')}
            onClick={() => {
              navigate('/manager/transactions/view', {
                state: {
                  transactionId: rowData?.id,
                  transactionDetails: rowData
                }
              })
            }}
          />
          {type !== 'opening_balance' && (
            <CancelIcon
              onClick={() => {
                setTransId(rowData?.id)
                setTransNumber(rowData?.journal_entry_number)
                setConfirmationDialogVisible(true)
              }}
              title={t('cancel')}
            />
          )}
        </>
      )}

      {rowData?.status === 'CANCELLED' && type !== 'opening_balance' && (
        <UndoIcon
          onClick={() => {
            setTransId(rowData?.id)
            setTransNumber(rowData?.journal_entry_number)
            setUndoDialogVisible(true)
          }}
          title={t('undo_reverse')}
        />
      )}
    </div>
  )
  const handleEdit = rowData => {
    // if (rowData?.is_locked) {
    //   toast.error(t("You can't edit the opening balance"));
    //   return;
    // }
    navigate(
      type === 'opening_balance'
        ? '/manager/opening-balances'
        : '/manager/transactions/create',
      {
        state: { transactionData: rowData, activeTab: type }
      }
    )
  }

  const onPageChangeEvent = (newPage, newRowsPerPage) => {
    if (onPageChange) {
      onPageChange(newPage, newRowsPerPage)
      return
    }
    const commonParams = {
      page: newPage,
      per_page: newRowsPerPage,
      ...(status ? { status } : {}),
      ...(type === 'his'
        ? { entry_number_prefix: 'INV' }
        : type === 'pharmacy'
        ? { entry_number_prefix: 'PH' }
        : type === 'supply_chain'
        ? { entry_number_prefix: 'SC' }
        : type === 'opening_balance'
        ? { entry_number_prefix: 'OP' }
        : { entry_number_prefix: 'JV' })
    }

    if (dates?.start_date && dates?.end_date) {
      dispatch(
        searchDatedTransaction({
          ...commonParams,
          ...dates
        })
      )
    } else if (searchQuery.length > 0) {
      dispatch(
        searchTransaction({
          ...commonParams,
          query: searchQuery
        })
      )
    } else {
      dispatch(getTransactions(commonParams))
    }
  }

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (page - 1) * per_page
  }

  const balanceBody = data => {
    return (
      <>
        {data?.total_debit}
        {/* {t('SAR')} */}
      </>
    )
  }
  const balanceBodyy = data => {
    return (
      <>
        {data?.total_credit}
        {/* {t('SAR')} */}
      </>
    )
  }

  const transactionTableColumns = [
    {
      body: templateBody,
      header: t('actions')
    },
    {
      field: 'date',
      header: t('entry_date'),
      body: rowData => moment(rowData.date).format('DD-MM-YYYY')
    },
    {
      field: 'journal_entry_number',
      header: t('entry_number')
    },
    {
      field: 'total_debit',
      body: balanceBody,
      header: t('Total Debit')
    },
    {
      field: 'total_credit',
      body: balanceBodyy,
      header: t('Total Credit')
    },
    {
      field: 'description',
      header: t('description')
    },
    {
      field: 'created_at',
      header: t('Created At'),
      body: rowData => (
        <div>
          <div>{moment(rowData.created_at).format('DD-MM-YYYY')}</div>
          <div className='ps-4'>
            {moment(rowData.created_at).format('HH:mm:ss')}
          </div>
        </div>
      )
    },
    {
      field: 'created_by_user_name',
      header: t('Created By')
    },
    {
      field: 'status',
      header: t('status'),
      body: rowData => {
        const status = getStatusConfig(rowData?.status)
        return (
          <CustomChip
            label={status.label}
            chipType={status.chipType}
            className='!h-[20px]'
          />
        )
      }
    }
  ]

  const viewTansationColumns = [
    {
      field: lang === 'en' ? `account_name_en` : `account_name_ar`,
      header: t('AccountName'),
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    },
    {
      field: 'account_code',
      header: t('Account Code'),
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    },
    {
      field: lang === 'en' ? `cost_center_name_en` : `cost_center_name_ar`,
      header: t('costCenter'),
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    },
    {
      header: t('Branch'),
      body: data => {
        return lang === 'en'
          ? data?.branch_name_en || data?.location_name_en || ''
          : data?.branch_name_ar || data?.location_name_ar || ''
      },
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    },
    {
      field: 'amount',
      header: t('debitAmount'),
      body: rowData =>
        rowData?.entry_type.toLowerCase() === 'debit'
          ? `${rowData?.amount}`
          : '0',
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    },
    {
      field: 'payment_method',
      header: t('creditAmount'),
      body: rowData =>
        rowData?.entry_type.toLowerCase() === 'credit'
          ? `${rowData?.amount}`
          : '0',
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    },
    {
      field: 'description',
      header: t('description'),
      pt: {
        headercontent: {
          className: '!p-[10px]'
        }
      },
      bodyCellClass: '!p-[15px]'
    }
  ]

  const refreshList = () => {
    const commonParams = {
      page: 1,
      per_page: 10,
      ...(status ? { status } : {}),
      ...(type === 'his'
        ? { entry_number_prefix: 'INV' }
        : type === 'pharmacy'
        ? { entry_number_prefix: 'PH' }
        : type === 'supply_chain'
        ? { entry_number_prefix: 'SC' }
        : type === 'opening_balance'
        ? { entry_number_prefix: 'OP' }
        : { entry_number_prefix: 'JV' })
    }
    if (dates?.start_date && dates?.end_date) {
      dispatch(searchDatedTransaction({ ...commonParams, ...dates }))
    } else if (searchQuery.length > 0) {
      dispatch(searchTransaction({ ...commonParams, query: searchQuery }))
    } else {
      dispatch(getTransactions(commonParams))
    }
    setTransId(null)
  }

  const handlePostTransaction = () => {
    setDeleteLoading(true)
    dispatch(postTransaction({ journal_entry_id: transId }))
      .then(res => {
        if (!res.error) {
          refreshList()
          setPostDialogVisible(false)
        }
      })
      .catch(console.log)
      .finally(() => setDeleteLoading(false))
  }

  const handleDeleteTransaction = () => {
    setDeleteLoading(true)
    dispatch(deleteTransactions({ journal_entry_id: transId }))
      .then(() => {
        refreshList()
        setConfirmationDialogVisible(false)
      })
      .catch(console.log)
      .finally(() => setDeleteLoading(false))
  }

  const handleUndoTransaction = () => {
    setDeleteLoading(true)
    dispatch(redoTransaction({ journal_entry_id: transId }))
      .then(res => {
        if (!res.error) {
          refreshList()
          setUndoDialogVisible(false)
        }
      })
      .catch(console.log)
      .finally(() => setDeleteLoading(false))
  }

  return (
    <div>
      <CustomTable
        data={transactions}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={transactionTableColumns}
        rows={per_page}
        rowClassName={rowData =>
          rowData?.status === 'CANCELLED'
            ? '[&_td]:!bg-red-50'
            : rowData?.status === 'POSTED'
            ? '[&_td]:!bg-green-50'
            : rowData?.status === 'DRAFT'
            ? '[&_td]:!bg-amber-50'
            : ''
        }
      />
      <Dialog
        header={t('Entries')}
        visible={createDialogVisible}
        onHide={() => {
          if (!createDialogVisible) return
          setCreateDialogVisible(false)
        }}
        pt={{
          root: { className: 'w-full lg:w-[80%] mx-4 overflow-auto' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-3' },
          closeButton: {
            className: 'hover:!bg-black/20 !outline-none !shadow-none'
          },
          closeButtonIcon: { className: '!text-white' }
        }}
      >
        <CustomTable
          data={selectedTransactionEntries}
          columns={viewTansationColumns}
          rows={per_page}
        />
      </Dialog>
      <ConfirmationModal
        visible={postDialogVisible}
        setVisible={setPostDialogVisible}
        header={t('Confirm Post')}
        message={t('Are you sure you want to post transaction number', {
          number: transNumber
        })}
        acceptLabel={t('Post')}
        rejectLabel={t('Cancel')}
        onAccept={handlePostTransaction}
        onReject={() => setPostDialogVisible(false)}
        Icon={MdOutlineCancel}
        isLoading={deleteLoading}
      />
      <ConfirmationModal
        visible={confirmationDialogVisible}
        setVisible={setConfirmationDialogVisible}
        header='Confirm Cancellation'
        message={t('Are you sure you want to cancel transaction number', {
          number: transNumber
        })}
        acceptLabel='Submit'
        rejectLabel='Cancel'
        onAccept={handleDeleteTransaction}
        onReject={() => setConfirmationDialogVisible(false)}
        Icon={MdOutlineCancel}
        isLoading={deleteLoading}
      />
      <ConfirmationModal
        visible={undoDialogVisible}
        setVisible={setUndoDialogVisible}
        header={t('undo_reverse_confirm_header')}
        message={t('undo_reverse_confirm_message', { number: transNumber })}
        acceptLabel={t('Submit')}
        rejectLabel={t('Cancel')}
        onAccept={handleUndoTransaction}
        onReject={() => setUndoDialogVisible(false)}
        Icon={MdOutlineCancel}
        isLoading={deleteLoading}
      />
      {transactions?.length > 0 && (
        <CustomPagination
          currentPage={page}
          rowsPerPage={per_page}
          totalRecords={total_pages}
          totalRecord={total_record}
          onPageChange={onPageChange}
        />
      )}
    </div>
  )
}
